/**
 * HTTP Service
 * Wrapper around axios for API calls with type safety
 */
import axios from 'axios';
import apiClient from '@/lib/axios';
import { ApiResponse, PaginatedResponse, PaginationParams } from '@/types';

export class HttpService {
  /**
   * GET request
   */
  static async get<T = unknown>(url: string, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.get(url, config);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * GET paginated request
   */
  static async getPaginated<T = unknown>(
    url: string,
    params?: PaginationParams,
    signal?: AbortSignal,
  ): Promise<PaginatedResponse<T>> {
    try {
      const response = await apiClient.get(url, { params, signal });
      return response.data;
    } catch (error: any) {
      // Let abort errors bubble up silently — react-query handles them.
      if (axios.isCancel(error) || error?.name === 'AbortError' || error?.code === 'ERR_CANCELED') {
        throw error;
      }
      throw this.handleError(error);
    }
  }

  /**
   * POST request
   */
  static async post<T = unknown>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.post(url, data, config);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * PUT request
   */
  static async put<T = unknown>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.put(url, data, config);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * PATCH request
   */
  static async patch<T = unknown>(url: string, data?: any, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.patch(url, data, config);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * DELETE request
   */
  static async delete<T = unknown>(url: string, config?: any): Promise<ApiResponse<T>> {
    try {
      const response = await apiClient.delete(url, config);
      return response.data;
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  /**
   * Handle API errors
   */
  private static handleError(error: any) {
    if (error.response) {
      // Server responded with error status
      const data = error.response.data;
      return {
        status: error.response.status,
        message: data?.message || 'An error occurred',
        errors: data?.errors,
        code: data?.code,
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        status: 0,
        message: 'No response from server',
        code: 'NO_RESPONSE',
      };
    } else {
      // Error in request setup
      return {
        status: 0,
        message: error.message || 'An error occurred',
        code: 'ERROR',
      };
    }
  }
}

export default HttpService;
